/**
 * nurses.service.ts
 *
 * Thin wrapper over GET /v5/nurses, scoped by the X-Target-Company header
 * injected by apiClient. Mirrors the doctors / coordinators service pattern.
 *
 * The server returns snake_case fields (first_name / last_name) via
 * NursesController::index; we re-map to camelCase for the V5 UI.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface Nurse {
  nurseID: number;
  firstName: string;
  lastName: string;
  suffix?: string;
  active: boolean;
}

interface NurseApiRow {
  nurseID: number;
  first_name?: string;
  last_name?: string;
  suffix?: string;
  active?: boolean;
}

export interface ListNursesFilters {
  /** Pass `false` for ONLY inactive nurses. Omit (or `true`) for active-only. */
  active?: boolean;
  /**
   * Nurse ids the caller's record already holds. They come back whatever their
   * state — flagged `active: false` — so an assigned-but-departed provider still
   * renders in the field that stores them. See lib/staffOptions.
   */
  includeIds?: number[];
}

export function listNurses(filters: ListNursesFilters = {}): Promise<Nurse[]> {
  const params: Record<string, string | number | boolean> = {};
  if (filters.active !== undefined) params.active = filters.active;
  if (filters.includeIds?.length) params.includeIds = filters.includeIds.join(',');

  return apiClient
    .get<ApiResponse<NurseApiRow[]>>('/nurses', { params })
    .then((r) =>
      (r.data.data ?? []).map((n) => ({
        nurseID: n.nurseID,
        firstName: n.first_name ?? '',
        lastName: n.last_name ?? '',
        suffix: n.suffix ?? '',
        active: !!n.active,
      })),
    );
}

export function formatNurseName(n: Pick<Nurse, 'firstName' | 'lastName'>): string {
  return `${n.firstName ?? ''} ${n.lastName ?? ''}`.trim();
}
